import { useEffect, useState } from 'react';
import { employeeApi } from '../../lib/api.js';
import { useToast } from '../../hooks/useToast.jsx';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/Card.jsx';
import Button from '../../components/ui/Button.jsx';
import Badge from '../../components/ui/Badge.jsx';
import { FileText, Download, AlertTriangle, Info } from 'lucide-react';

export default function MyContractPage() {
  const toast = useToast();
  const [contract, setContract] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function load() {
      try {
        const res = await employeeApi.getMyContract();
        setContract(res);
      } catch (err) {
        toast.error(`Failed to load contract: ${err.message}`);
      } finally {
        setLoading(false);
      }
    }
    load();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="h-6 w-6 animate-spin rounded-full border-[2.5px] border-border border-t-primary" />
      </div>
    );
  }

  const daysUntilExpiry = contract?.contractExpiryDate
    ? Math.ceil((new Date(contract.contractExpiryDate) - new Date()) / 86400000)
    : null;
  const isExpiring = daysUntilExpiry !== null && daysUntilExpiry <= 30 && daysUntilExpiry > 0;
  const isExpired = daysUntilExpiry !== null && daysUntilExpiry <= 0;

  return (
    <div className="max-w-2xl mx-auto space-y-6">
      <div>
        <h1 className="text-2xl font-bold tracking-tight text-foreground">My Contract</h1>
        <p className="mt-1 text-sm text-muted-foreground">View and download your employment contract</p>
      </div>

      {!contract?.hasContract ? (
        <Card>
          <CardContent className="flex items-start gap-3 py-8">
            <Info className="mt-0.5 h-5 w-5 text-muted-foreground shrink-0" />
            <div>
              <p className="text-sm font-medium text-foreground">No contract uploaded yet</p>
              <p className="text-xs text-muted-foreground mt-1">
                Contact your admin or HR department to have your contract uploaded to the system.
              </p>
            </div>
          </CardContent>
        </Card>
      ) : (
        <>
          {/* Expiry warning */}
          {isExpired && (
            <Card className="border-destructive/30 bg-destructive/5">
              <CardContent className="flex items-start gap-3 py-4">
                <AlertTriangle className="mt-0.5 h-4 w-4 text-destructive shrink-0" />
                <div>
                  <p className="text-sm font-medium text-destructive">Contract expired</p>
                  <p className="text-xs text-muted-foreground mt-0.5">
                    Your contract expired on {contract.contractExpiryDate}. Please contact HR for renewal.
                  </p>
                </div>
              </CardContent>
            </Card>
          )}
          {isExpiring && (
            <Card className="border-warning/30 bg-warning/5">
              <CardContent className="flex items-start gap-3 py-4">
                <AlertTriangle className="mt-0.5 h-4 w-4 text-warning shrink-0" />
                <div>
                  <p className="text-sm font-medium text-warning">Contract expiring soon</p>
                  <p className="text-xs text-muted-foreground mt-0.5">
                    Your contract expires in {daysUntilExpiry} day{daysUntilExpiry !== 1 ? 's' : ''} ({contract.contractExpiryDate}). Contact HR if renewal is needed.
                  </p>
                </div>
              </CardContent>
            </Card>
          )}

          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <FileText className="h-5 w-5 text-primary" />
                Employment Contract
              </CardTitle>
              <CardDescription>Your current employment contract on file</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <div className="text-xs text-muted-foreground uppercase tracking-wider mb-1">File Name</div>
                  <div className="font-medium font-mono text-xs">{contract.contractFileName}</div>
                </div>
                <div>
                  <div className="text-xs text-muted-foreground uppercase tracking-wider mb-1">Uploaded</div>
                  <div>{contract.contractUploadedAt
                    ? new Date(contract.contractUploadedAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
                    : '—'}</div>
                </div>
                <div>
                  <div className="text-xs text-muted-foreground uppercase tracking-wider mb-1">Expiry Date</div>
                  <div className="flex items-center gap-2">
                    {contract.contractExpiryDate || '—'}
                    {isExpired && <Badge variant="danger">Expired</Badge>}
                    {isExpiring && <Badge variant="warning">{daysUntilExpiry}d left</Badge>}
                    {!isExpired && !isExpiring && contract.contractExpiryDate && <Badge variant="success">Active</Badge>}
                  </div>
                </div>
                <div>
                  <div className="text-xs text-muted-foreground uppercase tracking-wider mb-1">Status</div>
                  <div>
                    {isExpired ? (
                      <Badge variant="danger">Expired</Badge>
                    ) : isExpiring ? (
                      <Badge variant="warning">Expiring Soon</Badge>
                    ) : (
                      <Badge variant="success">Active</Badge>
                    )}
                  </div>
                </div>
              </div>

              {contract.downloadUrl && (
                <a
                  href={contract.downloadUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 rounded-lg bg-primary px-5 py-2.5 text-sm font-semibold text-primary-foreground shadow-sm transition-all hover:shadow-md active:scale-[0.98]"
                >
                  <Download className="h-4 w-4" />
                  Download Contract
                </a>
              )}
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
}
